import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, 
  X, 
  Sparkles, 
  Code2,
  TrendingUp,
  Briefcase,
  MessageCircle,
  Calculator,
  Mail,
  Home,
  ChevronDown,
  Check,
  Globe2,
  Cpu,
  ShieldCheck,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import { Language, AppPage } from '../types';
import { translations } from '../data/translations';
import { WHATSAPP_PHONE_RAW, WHATSAPP_PHONE_DISPLAY, createWhatsAppUrl } from '../utils/whatsapp';
import { SorenaLogo } from './SorenaLogo';

interface NavbarProps {
  lang: Language;
  onLanguageChange: (newLang: Language) => void;
  onOpenQuote: () => void;
  currentPage: AppPage;
  onNavigate: (page: AppPage) => void;
}

// Crisp Vector SVG Flags for cross-platform perfection
const FlagIran: React.FC<{ className?: string }> = ({ className = "w-5 h-3.5" }) => (
  <svg className={`${className} rounded-[2px] shadow-sm shrink-0`} viewBox="0 0 640 400" aria-label="Iran">
    <rect width="640" height="133.3" fill="#239f40"/>
    <rect y="133.3" width="640" height="133.3" fill="#ffffff"/>
    <rect y="266.6" width="640" height="133.4" fill="#da0000"/>
    {/* Stylized emblem center */}
    <circle cx="320" cy="200" r="22" fill="#da0000" opacity="0.85"/>
  </svg>
);

const FlagUK: React.FC<{ className?: string }> = ({ className = "w-5 h-3.5" }) => (
  <svg className={`${className} rounded-[2px] shadow-sm shrink-0`} viewBox="0 0 640 400" aria-label="United Kingdom">
    <rect width="640" height="400" fill="#012169"/>
    <path d="M0 0 L640 400 M640 0 L0 400" stroke="#ffffff" strokeWidth="60"/>
    <path d="M0 0 L640 400 M640 0 L0 400" stroke="#c8102e" strokeWidth="35"/>
    <path d="M320 0 V400 M0 200 H640" stroke="#ffffff" strokeWidth="95"/>
    <path d="M320 0 V400 M0 200 H640" stroke="#c8102e" strokeWidth="55"/>
  </svg>
);

export const Navbar: React.FC<NavbarProps> = ({ 
  lang, 
  onLanguageChange, 
  onOpenQuote,
  currentPage,
  onNavigate
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const menuDrawerRef = useRef<HTMLDivElement>(null);
  const hamburgerBtnRef = useRef<HTMLButtonElement>(null);

  const t = translations[lang];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns and drawers on outside click / tap
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!target) return;

      // Close Language Dropdown if clicked outside its container
      if (langDropdownRef.current && !langDropdownRef.current.contains(target)) {
        setLangDropdownOpen(false);
      }

      // Close Universal Nav Drawer if clicked outside the drawer and outside the hamburger button
      if (
        menuDrawerRef.current &&
        !menuDrawerRef.current.contains(target) &&
        (!hamburgerBtnRef.current || !hamburgerBtnRef.current.contains(target))
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('pointerdown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Close menu & dropdowns on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setLangDropdownOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Navigation page routes
  const navLinks: {
    id: AppPage;
    label: string;
    description: string;
    badge?: string;
    icon: React.ElementType;
    highlight?: boolean;
  }[] = [
    {
      id: 'home',
      label: lang === 'fa' ? 'صفحه اصلی' : 'Home',
      description: lang === 'fa' ? 'معرفی خدمات و توانمندی‌های سورنا آی‌تی' : 'Overview & Services',
      icon: Home,
    },
    {
      id: 'campaign',
      label: lang === 'fa' ? 'کمپین تخفیف هرمی (۱۰۰ به ۳۰ میلیون)' : 'Pyramid Campaign (70% OFF)',
      description: lang === 'fa' ? 'کاهش هزینه طراحی سایت با معرفی ۲ مشتری' : 'Web design reduced to 30M Toman with 2 referrals',
      badge: lang === 'fa' ? '۷۰٪ تخفیف ویژه' : '70% Special Discount',
      icon: Sparkles,
      highlight: true,
    },
    {
      id: 'services',
      label: t.nav.services,
      description: lang === 'fa' ? 'طراحی سایت مدرن، فروشگاه و سئو فنی' : 'Modern Web Design & Core SEO',
      icon: Code2,
    },
    {
      id: 'algo-trading',
      label: lang === 'fa' ? 'کدنویسی فارکس و متاتریدر (MQL5)' : 'Forex Algorithmic (MQL5 / Pine)',
      description: lang === 'fa' ? 'اکسپرت ادوایزر خودکار، پل تریدینگ‌ویو و پراپ' : 'Automated EAs & TradingView Webhooks',
      icon: TrendingUp,
    },
    {
      id: 'portfolio',
      label: t.nav.portfolio,
      description: lang === 'fa' ? 'نمونه کارهای اجرایی و پروژه‌های تحویل‌شده' : 'Showcase of Delivered Projects',
      icon: Briefcase,
    },
    {
      id: 'tech-comparison',
      label: lang === 'fa' ? 'مقایسه تکنولوژی‌ها (کد در برابر وردپرس)' : 'Tech Stack Matrix',
      description: lang === 'fa' ? 'مقایسه سرعت، امنیت، سئو و توسعه‌پذیری' : 'Performance, Security & Scalability Comparison',
      icon: Cpu,
    },
    {
      id: 'calculator',
      label: lang === 'fa' ? 'محاسبه‌گر آنلاین هزینه و زمان' : 'Interactive Price Calculator',
      description: lang === 'fa' ? 'استعلام آنی برآورد قیمت پروژه و پیش‌فاکتور' : 'Instant estimate of timeline & budget',
      icon: Calculator,
    },
    {
      id: 'blog',
      label: lang === 'fa' ? 'وبلاگ و استراتژی سئو' : 'SEO & Knowledge Hub',
      description: lang === 'fa' ? 'مقالات تخصصی، خوشه‌های کلمات کلیدی و راهنماهای معتبر' : 'Technical guides, cost analysis & local SEO',
      icon: BookOpen,
    },
    {
      id: 'why-us',
      label: lang === 'fa' ? 'چرا سورنا آی‌تی و نظرات مشتریان' : 'Why Sorena IT & Reviews',
      description: lang === 'fa' ? 'مزایای رقابتی، آپ‌تایم ۹۹.۹٪ و رضایت کارفرمایان' : '99.9% Uptime, Clean Code & Client Satisfaction',
      icon: ShieldCheck,
    },
    {
      id: 'faq',
      label: lang === 'fa' ? 'سوالات متداول کارفرمایان' : 'Frequently Asked Questions',
      description: lang === 'fa' ? 'پاسخ به سوالات پیرامون قرارداد، پشتیبانی و سورس‌کد' : 'Answers regarding contracts, support & source code',
      icon: HelpCircle,
    },
    {
      id: 'contact',
      label: lang === 'fa' ? 'تماس مستقیم و ثبت سفارش' : 'Contact & Submit Order',
      description: lang === 'fa' ? 'مشاوره تلفنی، واتس‌اپ و تلگرام مهندس کاویانی‌پور' : 'Direct consultation with Arash Kavianipoor',
      icon: Mail,
    },
  ];

  const languages: { 
    code: Language; 
    label: string; 
    nativeLabel: string;
    flagComponent: React.FC<{ className?: string }>;
  }[] = [
    { 
      code: 'fa', 
      label: 'فارسی', 
      nativeLabel: 'فارسی (FA)', 
      flagComponent: FlagIran 
    },
    { 
      code: 'en', 
      label: 'English', 
      nativeLabel: 'English (EN)', 
      flagComponent: FlagUK 
    },
  ];

  const currentLangObj = languages.find((l) => l.code === lang) || languages[0];
  const CurrentFlag = currentLangObj.flagComponent;

  const whatsappDirectUrl = createWhatsAppUrl(
    lang === 'fa'
      ? 'سلام جناب مهندس کاویانی‌پور، جهت مشاوره و ثبت سفارش طراحی سایت / اکسپرت پیام می‌دهم.'
      : 'Hello Mr. Kavianipoor, I am contacting you regarding web design / MQL5 algorithmic trading services.'
  );

  const handleNavClick = (pageId: AppPage) => {
    onNavigate(pageId);
    setMenuOpen(false);
  };

  return (
    <header 
      id="main-header"
      className={`hidden lg:block fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        isScrolled 
          ? 'bg-[#100804]/95 backdrop-blur-md border-b border-amber-900/40 shadow-lg shadow-amber-950/40 py-2' 
          : 'bg-[#0c0704]/90 backdrop-blur-sm border-b border-amber-950/60 py-2.5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-9 sm:h-10">
          
          {/* 1. Ultra-Clean Lightweight Logo with Gold Glow */}
          <div className="flex items-center gap-3 shrink-0">
            <button 
              onClick={() => handleNavClick('home')}
              className="flex items-center gap-2.5 group focus:outline-none cursor-pointer"
              aria-label="Sorena IT"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-amber-400 via-yellow-400 to-amber-600 p-[1px] shadow-md shadow-amber-500/25 group-hover:scale-105 transition-transform shrink-0">
                <div className="w-full h-full bg-[#100804] rounded-[7px] p-1 flex items-center justify-center">
                  <SorenaLogo className="w-full h-full" glow />
                </div>
              </div>

              <div className="flex flex-col text-start">
                <span className="text-sm font-black tracking-tight text-white group-hover:text-amber-300 transition-colors leading-none">
                  {lang === 'fa' ? 'سورنا آی‌تی' : 'Sorena IT'}
                </span>
                <span className="text-[10px] font-mono text-amber-400/80 leading-none mt-1 hidden sm:block">
                  sorena-it.ir
                </span>
              </div>
            </button>
          </div>

          {/* 2. Right / End Controls: WhatsApp + Language Dropdown with Flags + Universal Hamburger Button */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            
            {/* Direct WhatsApp Order Fast-Link (Compact & Sleek) */}
            <a
              id="header-whatsapp-btn"
              href={whatsappDirectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-bold rounded-lg bg-[#241308]/90 hover:bg-[#341b0b] text-amber-200 border border-amber-600/50 hover:border-amber-400 transition-all shadow-sm shadow-amber-950/30"
              title={lang === 'fa' ? 'ارسال پیام و ثبت سفارش در واتس‌اپ: (+98)9128634908' : 'Direct WhatsApp Order & Consultation: (+98)9128634908'}
            >
              <MessageCircle className="w-3.5 h-3.5 text-amber-400 fill-amber-400/20 shrink-0" />
              <span className="hidden sm:inline">{lang === 'fa' ? 'واتس‌اپ:' : 'WhatsApp:'}</span>
              <span dir="ltr" className="font-mono text-[11px] font-bold">{WHATSAPP_PHONE_DISPLAY}</span>
            </a>

            {/* Quick Online Calculator Link */}
            <button
              id="header-quote-btn"
              type="button"
              onClick={() => handleNavClick('calculator')}
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-[#241308]/70 hover:bg-[#341b0b] text-amber-200 border border-amber-700/60 hover:border-amber-400 transition-all cursor-pointer"
            >
              <Calculator className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'fa' ? 'محاسبه آنلاین' : 'Calculator'}</span>
            </button>

            {/* Quick Blog / SEO Guides Link */}
            <button
              id="header-blog-btn"
              type="button"
              onClick={() => handleNavClick('blog')}
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-[#241308]/70 hover:bg-[#341b0b] text-amber-200 border border-amber-700/60 hover:border-amber-400 transition-all cursor-pointer"
            >
              <BookOpen className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'fa' ? 'وبلاگ سئو' : 'Articles'}</span>
            </button>

            {/* 3. Language Switcher: Interactive Dropdown with Vector Flags */}
            <div className="relative" ref={langDropdownRef}>
              <button
                id="language-dropdown-toggle"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLangDropdownOpen(!langDropdownOpen);
                  if (!langDropdownOpen) {
                    setMenuOpen(false);
                  }
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#180e07]/90 border border-amber-800/60 hover:border-amber-500/50 text-xs text-amber-100 transition-all cursor-pointer shadow-sm"
                aria-expanded={langDropdownOpen}
                aria-haspopup="true"
                aria-label="Select Language"
              >
                <CurrentFlag className="w-4 h-3" />
                <span className="font-medium text-[11px] hidden sm:inline">{currentLangObj.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-amber-400 transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {langDropdownOpen && (
                <div 
                  id="language-dropdown-menu"
                  className="absolute top-full mt-2 end-0 w-44 rounded-xl bg-[#160b05]/98 backdrop-blur-2xl border border-amber-700/60 shadow-2xl shadow-amber-950/90 p-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                  role="menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-[10px] font-bold text-amber-300/80 px-2 py-1 uppercase tracking-wider font-mono border-b border-amber-900/50 mb-1">
                    {lang === 'fa' ? 'انتخاب زبان' : 'Select Language'}
                  </div>
                  {languages.map((l) => {
                    const isSelected = lang === l.code;
                    const ItemFlag = l.flagComponent;
                    return (
                      <button
                        key={l.code}
                        type="button"
                        onClick={() => {
                          onLanguageChange(l.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer text-start ${
                          isSelected
                            ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40'
                            : 'text-stone-300 hover:bg-[#251307] hover:text-white'
                        }`}
                        role="menuitem"
                      >
                        <div className="flex items-center gap-2">
                          <ItemFlag className="w-4 h-3" />
                          <span>{l.nativeLabel}</span>
                        </div>
                        {isSelected && <Check className="w-3.5 h-3.5 text-amber-400" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 4. Universal Hamburger Menu Button (Active for BOTH Desktop & Mobile) */}
            <button
              ref={hamburgerBtnRef}
              id="main-hamburger-btn"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
                if (!menuOpen) {
                  setLangDropdownOpen(false);
                }
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all cursor-pointer shadow-md ${
                menuOpen
                  ? 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 text-stone-950 border-amber-300 shadow-amber-500/30'
                  : 'bg-[#221208] hover:bg-[#2d180b] text-amber-200 border-amber-700/60 hover:border-amber-400 shadow-amber-950/40'
              }`}
              aria-label={menuOpen ? 'Close Menu' : 'Open Menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? (
                <X className="w-4 h-4 text-stone-950" />
              ) : (
                <Menu className="w-4 h-4 text-amber-300" />
              )}
              <span className="text-xs font-black">
                {menuOpen 
                  ? (lang === 'fa' ? 'بستن' : 'Close') 
                  : (lang === 'fa' ? 'منو' : 'Menu')}
              </span>
            </button>

          </div>

        </div>
      </div>

      {/* 5. Modern Universal Flyout Menu Drawer for All Screens (Desktop & Mobile) */}
      {menuOpen && (
        <>
          {/* Backdrop Blur Overlay */}
          <div 
            id="universal-nav-backdrop"
            className="fixed inset-0 bg-stone-950/75 backdrop-blur-md z-40 animate-in fade-in duration-200 cursor-pointer"
            onClick={() => setMenuOpen(false)}
            onPointerDown={() => setMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Menu Drawer Panel */}
          <div 
            ref={menuDrawerRef}
            id="universal-nav-drawer"
            className="fixed top-12 sm:top-14 end-3 sm:end-6 w-[340px] sm:w-[400px] max-w-[calc(100vw-1.5rem)] max-h-[calc(100vh-4.5rem)] overflow-y-auto bg-[#140a04]/98 backdrop-blur-2xl border border-amber-700/60 rounded-2xl shadow-2xl shadow-amber-950/90 p-4 sm:p-5 z-50 animate-in fade-in slide-in-from-top-3 duration-200"
            role="dialog"
            aria-label="Navigation Menu"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between pb-3.5 border-b border-amber-900/50 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#0c0704] p-1 border border-amber-500/40 flex items-center justify-center shadow-sm shadow-amber-500/20">
                  <SorenaLogo className="w-full h-full" glow />
                </div>
                <div className="flex flex-col text-start">
                  <span className="text-xs font-bold text-white uppercase tracking-wider leading-none">
                    {lang === 'fa' ? 'ناوبری سورنا آی‌تی' : 'Sorena IT Navigation'}
                  </span>
                  <span className="text-[10px] font-mono text-amber-400/80 leading-none mt-1">sorena-it.ir</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="p-1 rounded-lg text-stone-400 hover:text-white hover:bg-amber-900/40 cursor-pointer transition-colors"
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Nav Items List */}
            <nav className="space-y-1.5" aria-label="Expanded Navigation Links">
              {navLinks.map((item) => {
                const isActive = currentPage === item.id;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    className={`w-full p-2.5 sm:p-3 rounded-xl transition-all flex items-start justify-between text-start cursor-pointer group ${
                      isActive
                        ? 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 text-stone-950 shadow-lg shadow-amber-500/25 font-black border border-amber-300'
                        : item.highlight
                        ? 'bg-[#241308] hover:bg-[#341b0b] border border-amber-500/50 text-amber-200 hover:border-amber-400'
                        : 'bg-[#180e07]/70 hover:bg-[#251307] text-stone-200 border border-amber-950 hover:border-amber-800'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg mt-0.5 shrink-0 ${
                        isActive 
                          ? 'bg-stone-950/20 text-stone-950' 
                          : item.highlight 
                          ? 'bg-amber-950 text-amber-300 border border-amber-500/40'
                          : 'bg-[#241308] text-amber-300 border border-amber-800/60 group-hover:text-amber-200'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>

                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs sm:text-sm font-bold ${isActive ? 'text-stone-950' : 'text-stone-100'}`}>
                            {item.label}
                          </span>
                          {item.badge && (
                            <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-950 text-[9px] font-black animate-pulse">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <p className={`text-[11px] leading-relaxed mt-0.5 ${isActive ? 'text-stone-900 font-medium' : 'text-stone-400'}`}>
                          {item.description}
                        </p>
                      </div>
                    </div>

                    {isActive && (
                      <Check className="w-4 h-4 text-stone-950 shrink-0 mt-2" />
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Quick Actions Footer inside Drawer */}
            <div className="pt-3.5 mt-3.5 border-t border-amber-900/50 space-y-2">
              <a
                href={whatsappDirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 px-3 rounded-xl bg-[#241308] hover:bg-[#341b0b] text-amber-200 border border-amber-600/60 text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-all"
              >
                <MessageCircle className="w-4 h-4 text-amber-400" />
                <span>{lang === 'fa' ? 'ثبت سفارش مستقیم در واتس‌اپ:' : 'Direct WhatsApp Order:'}</span>
                <span dir="ltr" className="font-mono font-bold">{WHATSAPP_PHONE_DISPLAY}</span>
              </a>

              <button
                type="button"
                onClick={() => handleNavClick('calculator')}
                className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 hover:from-amber-400 hover:to-yellow-300 text-stone-950 text-xs font-black flex items-center justify-center gap-2 shadow-md shadow-amber-500/25 cursor-pointer transition-all"
              >
                <Calculator className="w-3.5 h-3.5 text-stone-950" />
                <span>{lang === 'fa' ? 'محاسبه‌گر آنلاین هزینه و زمان سایت' : 'Online Price & Timeline Calculator'}</span>
              </button>
            </div>

          </div>
        </>
      )}
    </header>
  );
};

